import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
  Users, 
  FolderKanban, 
  Send, 
  MessageSquare, 
  BarChart3,
  TrendingUp,
  Activity,
  Plus,
  ArrowLeft,
  Sparkles,
  FileText
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getDashboardStats } from "@/lib/marketing-api";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

// Get activity data based on language
const getActivityData = (isRTL) => [
  { name: isRTL ? "السبت" : "Sat", messages: 4000, contacts: 2400 },
  { name: isRTL ? "الأحد" : "Sun", messages: 3000, contacts: 1398 },
  { name: isRTL ? "الاثنين" : "Mon", messages: 2000, contacts: 9800 },
  { name: isRTL ? "الثلاثاء" : "Tue", messages: 2780, contacts: 3908 },
  { name: isRTL ? "الأربعاء" : "Wed", messages: 1890, contacts: 4800 },
  { name: isRTL ? "الخميس" : "Thu", messages: 2390, contacts: 3800 },
  { name: isRTL ? "الجمعة" : "Fri", messages: 3490, contacts: 4300 },
];

const getPlatformData = (isRTL) => [
  { name: isRTL ? "تلجرام" : "Telegram", value: 45, color: "#3B82F6" },
  { name: isRTL ? "واتساب" : "WhatsApp", value: 25, color: "#22C55E" },
  { name: isRTL ? "فيسبوك" : "Facebook", value: 15, color: "#6366F1" },
  { name: isRTL ? "أخرى" : "Others", value: 15, color: "#8B5CF6" },
];

export default function Dashboard({ onNavigate }) {
  const [stats, setStats] = useState({
    total_contacts: 0,
    total_groups: 0,
    total_campaigns: 0,
    active_campaigns: 0,
    total_messages_sent: 0,
    total_polls: 0,
  });
  const [loading, setLoading] = useState(true);
  const { isRTL } = useLanguage();
  const { t } = useTranslation();
  
  const activityData = getActivityData(isRTL);
  const platformData = getPlatformData(isRTL);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await getDashboardStats();
      setStats(response.data || response);
    } catch (error) {
      console.error("Error loading stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (pageId) => {
    if (onNavigate) {
      onNavigate(pageId);
    }
  };

  const statCards = [
    {
      title: t('marketingFull.contacts'),
      value: stats.total_contacts,
      icon: Users,
      color: "from-blue-500 to-blue-600",
      textColor: "text-blue-400",
      navigateTo: "contacts"
    },
    {
      title: t('marketingFull.groups'),
      value: stats.total_groups,
      icon: FolderKanban,
      color: "from-green-500 to-emerald-600",
      textColor: "text-green-400",
      navigateTo: "groups"
    },
    {
      title: t('marketingFull.activeCampaigns'),
      value: stats.active_campaigns,
      icon: Send,
      color: "from-violet-500 to-purple-600",
      textColor: "text-violet-400",
      navigateTo: "campaigns"
    },
    {
      title: t('marketingFull.messagesSent'),
      value: stats.total_messages_sent,
      icon: MessageSquare,
      color: "from-amber-500 to-orange-600",
      textColor: "text-amber-400",
      navigateTo: "messages"
    },
    {
      title: t('marketingFull.polls'),
      value: stats.total_polls,
      icon: BarChart3,
      color: "from-pink-500 to-rose-600",
      textColor: "text-pink-400",
      navigateTo: "polls"
    },
    {
      title: t('marketingFull.totalCampaigns'),
      value: stats.total_campaigns,
      icon: Activity,
      color: "from-cyan-500 to-teal-600",
      textColor: "text-cyan-400",
      navigateTo: "campaigns"
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent">
            {t('marketingFull.dashboardTitle')}
          </h1>
          <p className="text-neutral-500 mt-1">{t('marketingFull.dashboardSubtitle')}</p>
        </div>
        <Button 
          onClick={() => handleNavigate('campaigns')}
          className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/30"
          data-testid="new-campaign-btn"
        >
          <Plus className="w-4 h-4 me-2" />
          {t('marketingFull.newCampaign')}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <button 
              key={index} 
              onClick={() => handleNavigate(stat.navigateTo)}
              className={`${isRTL ? 'text-right' : 'text-left'} w-full`}
            >
              <div 
                className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/10 p-6 hover:border-white/20 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/5"
                data-testid={`stat-card-${index}`}
              >
                {/* Background gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                
                <div className="relative flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-400">{stat.title}</p>
                    <p className={`text-3xl font-bold mt-2 ${stat.textColor}`}>
                      {stat.value.toLocaleString(isRTL ? "ar-SA" : "en-US")}
                    </p>
                  </div>
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                </div>
                
                <div className="flex items-center mt-4 text-sm">
                  <TrendingUp className="w-4 h-4 me-1 text-green-400" />
                  <span className="text-green-400 font-medium">+12%</span>
                  <span className="text-neutral-500 ms-2">{t('marketingFull.fromLastWeek')}</span>
                </div>

                {/* Hover arrow */}
                <div className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity`}>
                  <ArrowLeft className={`w-5 h-5 ${stat.textColor} ${!isRTL && 'rotate-180'}`} />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Chart */}
        <div className="lg:col-span-2 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/10 p-6" data-testid="activity-chart">
          <div className="flex items-center gap-2 mb-6">
            <Activity className="w-5 h-5 text-emerald-400" />
            <h3 className="font-semibold text-white">{t('marketingFull.campaignActivity')}</h3>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={activityData}>
                <defs>
                  <linearGradient id="colorMessages" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorContacts" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #334155',
                    borderRadius: '12px',
                    color: '#fff'
                  }} 
                />
                <Area 
                  type="monotone" 
                  dataKey="messages" 
                  stroke="#10B981" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorMessages)" 
                  name={t('marketingFull.messages')}
                />
                <Area 
                  type="monotone" 
                  dataKey="contacts" 
                  stroke="#6366F1" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorContacts)" 
                  name={t('marketingFull.contacts')}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Platform Distribution */}
        <div className="rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/10 p-6" data-testid="platform-chart">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="w-5 h-5 text-emerald-400" />
            <h3 className="font-semibold text-white">{t('marketingFull.platformDistribution')}</h3>
          </div>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={platformData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {platformData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #334155',
                    borderRadius: '12px',
                    color: '#fff'
                  }} 
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2">
            {platformData.map((platform, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: platform.color }}
                  />
                  <span className="text-neutral-400">{platform.name}</span>
                </div>
                <span className="font-medium text-white">{platform.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="rounded-2xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 p-6">
        <h3 className="font-semibold text-white mb-4">{t('marketingFull.quickActions')}</h3>
        <div className="flex flex-wrap gap-3">
          <Button 
            onClick={() => handleNavigate('campaigns')}
            variant="secondary"
            className="bg-white/10 hover:bg-white/20 text-white"
            data-testid="quick-action-campaign"
          >
            <Send className="w-4 h-4 me-2" />
            {t('marketingFull.newCampaign')}
          </Button>
          <Button 
            onClick={() => handleNavigate('contacts')}
            variant="secondary"
            className="bg-white/10 hover:bg-white/20 text-white"
            data-testid="quick-action-contacts"
          >
            <Users className="w-4 h-4 me-2" />
            {t('marketingFull.addContact')}
          </Button>
          <Button 
            onClick={() => handleNavigate('groups')}
            variant="secondary"
            className="bg-white/10 hover:bg-white/20 text-white"
            data-testid="quick-action-groups"
          >
            <FolderKanban className="w-4 h-4 me-2" />
            {t('marketingFull.createGroup')}
          </Button>
          <Button 
            onClick={() => handleNavigate('templates')}
            variant="secondary"
            className="bg-white/10 hover:bg-white/20 text-white"
            data-testid="quick-action-templates"
          >
            <FileText className="w-4 h-4 me-2" />
            {t('marketingFull.templatesTitle')}
          </Button>
          <Button 
            onClick={() => handleNavigate('ai')}
            variant="secondary"
            className="bg-white/10 hover:bg-white/20 text-white"
            data-testid="quick-action-ai"
          >
            <Sparkles className="w-4 h-4 me-2" />
            {t('marketingFull.aiAssistantTitle')}
          </Button>
          <Button 
            onClick={() => handleNavigate('polls')}
            variant="secondary"
            className="bg-white/10 hover:bg-white/20 text-white"
            data-testid="quick-action-polls"
          >
            <BarChart3 className="w-4 h-4 me-2" />
            {t('marketingFull.createPoll')}
          </Button>
          <Button 
            onClick={() => handleNavigate('broadcast')}
            variant="secondary"
            className="bg-white/10 hover:bg-white/20 text-white"
            data-testid="quick-action-broadcast"
          >
            <MessageSquare className="w-4 h-4 me-2" />
            {t('marketingFull.broadcastTitle')}
          </Button>
        </div>
      </div>
    </div>
  );
}
